import { useEffect, useState } from 'react';
import { useNavigate } from 'react-router-dom';
import { Card, Field, PrimaryButton, inputClass } from '@/components/Card';
import { Chip, ChipRow } from '@/components/ChipRow';
import { api } from '@/lib/api';
import { useAuth } from '@/lib/auth';
import type { Airport, Service } from '@shared/types';

export function AdminPage() {
  const { user, setUser } = useAuth();
  const navigate = useNavigate();
  const [adminPass, setAdminPass] = useState('');
  const [airports, setAirports] = useState<Airport[]>([]);
  const [services, setServices] = useState<Service[]>([]);
  const [error, setError] = useState<string | null>(null);
  const [iata, setIata] = useState('');
  const [name, setName] = useState('');
  const [city, setCity] = useState('');
  const [summary, setSummary] = useState('');
  const [source, setSource] = useState('');
  const [svcAirport, setSvcAirport] = useState('EWR');
  const [svcSlug, setSvcSlug] = useState('');
  const [svcName, setSvcName] = useState('');
  const [svcKind, setSvcKind] = useState('lounge');
  const [svcDesc, setSvcDesc] = useState('');
  const [svcBookingUrl, setSvcBookingUrl] = useState('');
  const [svcSource, setSvcSource] = useState('');

  async function load() {
    const data = await api.adminCatalog();
    setAirports(data.airports);
    setServices(data.services);
  }

  useEffect(() => {
    if (user?.admin) load().catch((err) => setError(err instanceof Error ? err.message : 'Admin catalog failed.'));
  }, [user?.admin]);

  if (!user) return null;

  if (!user.admin && user.mode === 'guest') {
    return (
      <Card className="space-y-4">
        <h2 className="font-display text-[30px] leading-none text-navy">Admin unlock</h2>
        <p className="text-sm text-muted">Sign in with the household passphrase first.</p>
        <PrimaryButton
          type="button"
          onClick={async () => {
            await api.logout();
            setUser(null);
            navigate('/');
          }}
        >
          Sign in as household
        </PrimaryButton>
      </Card>
    );
  }

  if (!user.admin) {
    return (
      <Card className="space-y-4">
        <h2 className="font-display text-[30px] leading-none text-navy">Admin unlock</h2>
        <p className="text-sm text-muted">Review-and-publish is gated by a separate admin passphrase. Guest users can still plan trips.</p>
        <Field label="Admin passphrase">
          <input className={inputClass} type="password" value={adminPass} onChange={(e) => setAdminPass(e.target.value)} />
        </Field>
        <PrimaryButton
          onClick={async () => {
            try {
              const data = await api.adminUnlock(adminPass);
              setUser(data.user);
              setError(null);
            } catch (err) {
              setError(err instanceof Error ? err.message : 'Unlock failed.');
            }
          }}
        >
          Unlock admin
        </PrimaryButton>
        {error && <p className="text-sm text-alert">{error}</p>}
      </Card>
    );
  }

  return (
    <div className="space-y-4">
      <Card>
        <h2 className="font-display text-[30px] leading-none text-navy">Review and publish</h2>
        <p className="mt-2 text-sm text-muted">
          New airports and services start as draft. Require a source. Prefer UNVERIFIED or omit over fabricating a live price.
        </p>
      </Card>

      <Card className="space-y-3">
        <h3 className="font-display text-2xl text-navy">Add airport</h3>
        <Field label="IATA">
          <input className={inputClass} value={iata} onChange={(e) => setIata(e.target.value)} />
        </Field>
        <Field label="Name">
          <input className={inputClass} value={name} onChange={(e) => setName(e.target.value)} />
        </Field>
        <Field label="City">
          <input className={inputClass} value={city} onChange={(e) => setCity(e.target.value)} />
        </Field>
        <Field label="Playbook summary">
          <textarea className={inputClass} rows={3} value={summary} onChange={(e) => setSummary(e.target.value)} />
        </Field>
        <Field label="Source (required)">
          <input className={inputClass} value={source} onChange={(e) => setSource(e.target.value)} placeholder="URL or playbook citation" />
        </Field>
        <PrimaryButton
          onClick={async () => {
            await api.addAirport({ iata, name, city, playbookSummary: summary, source, confidence: 'unverified' });
            setIata('');
            setName('');
            setCity('');
            setSummary('');
            setSource('');
            await load();
          }}
        >
          Save as draft
        </PrimaryButton>
      </Card>

      <Card className="space-y-3">
        <h3 className="font-display text-2xl text-navy">Add service</h3>
        <Field label="Airport">
          <select className={inputClass} value={svcAirport} onChange={(e) => setSvcAirport(e.target.value)}>
            {airports.map((airport) => (
              <option key={airport.id}>{airport.iata}</option>
            ))}
          </select>
        </Field>
        <Field label="Slug">
          <input className={inputClass} value={svcSlug} onChange={(e) => setSvcSlug(e.target.value)} placeholder="centurion" />
        </Field>
        <Field label="Name">
          <input className={inputClass} value={svcName} onChange={(e) => setSvcName(e.target.value)} />
        </Field>
        <Field label="Kind">
          <select className={inputClass} value={svcKind} onChange={(e) => setSvcKind(e.target.value)}>
            <option value="lounge">lounge</option>
            <option value="security">security</option>
            <option value="concierge">concierge</option>
            <option value="rest_suite">rest_suite</option>
            <option value="ground">ground</option>
          </select>
        </Field>
        <Field label="Description">
          <textarea className={inputClass} rows={3} value={svcDesc} onChange={(e) => setSvcDesc(e.target.value)} />
        </Field>
        <Field label="Booking URL (optional, official reserve page)">
          <input
            className={inputClass}
            value={svcBookingUrl}
            onChange={(e) => setSvcBookingUrl(e.target.value)}
            placeholder="https://minutesuites.com/locations/…"
          />
        </Field>
        <Field label="Source (required)">
          <input className={inputClass} value={svcSource} onChange={(e) => setSvcSource(e.target.value)} />
        </Field>
        <PrimaryButton
          onClick={async () => {
            await api.addService({
              airportIata: svcAirport,
              slug: svcSlug,
              name: svcName,
              kind: svcKind,
              description: svcDesc,
              bookingUrl: svcBookingUrl || null,
              source: svcSource,
              confidence: 'unverified',
            });
            setSvcSlug('');
            setSvcName('');
            setSvcDesc('');
            setSvcBookingUrl('');
            setSvcSource('');
            await load();
          }}
        >
          Save as draft
        </PrimaryButton>
      </Card>

      <Card>
        <h3 className="font-display text-2xl text-navy">Airports</h3>
        <ul className="mt-3 space-y-3">
          {airports.map((airport) => (
            <li key={airport.id}>
              <ChipRow>
                <Chip tone="navy">{airport.iata}</Chip>
                <Chip tone={airport.status === 'published' ? 'success' : 'brass'}>{airport.status}</Chip>
              </ChipRow>
              <p className="mt-1 text-sm">{airport.name}</p>
              <div className="mt-2 flex flex-wrap gap-2">
                <button className="rounded-chip border border-line px-2 py-1 text-[11px] font-semibold" onClick={() => api.setAirportStatus(airport.id, 'review').then(load)}>
                  Review
                </button>
                <button className="rounded-chip bg-navy px-2 py-1 text-[11px] font-semibold text-sand" onClick={() => api.setAirportStatus(airport.id, 'published').then(load)}>
                  Publish
                </button>
              </div>
            </li>
          ))}
        </ul>
      </Card>

      <Card>
        <h3 className="font-display text-2xl text-navy">Services</h3>
        <ul className="mt-3 space-y-3">
          {services.map((service) => (
            <li key={service.id}>
              <ChipRow>
                <Chip>{service.airportIata}</Chip>
                <Chip tone={service.status === 'published' ? 'success' : 'brass'}>{service.status}</Chip>
                <Chip>{service.kind}</Chip>
              </ChipRow>
              <p className="mt-1 text-sm font-semibold">{service.name}</p>
              <p className="text-[12px] text-muted">{service.provenance.source}</p>
              <div className="mt-2 flex flex-wrap gap-2">
                <button className="rounded-chip border border-line px-2 py-1 text-[11px] font-semibold" onClick={() => api.setServiceStatus(service.id, 'review').then(load)}>
                  Review
                </button>
                <button className="rounded-chip bg-navy px-2 py-1 text-[11px] font-semibold text-sand" onClick={() => api.setServiceStatus(service.id, 'published').then(load)}>
                  Publish
                </button>
              </div>
            </li>
          ))}
        </ul>
      </Card>
      {error && <p className="text-sm text-alert">{error}</p>}
    </div>
  );
}
